import React, { useState } from 'react'
import { positionDefault } from '@reach/popover'
import {
  Filter,
  SelectTriggerUnstyled,
  Separator,
  Dropdown,
  Checkbox,
  Box,
} from '@sketch/components'
import {
  Wrapper,
  Options,
  ToggleWrapper,
  StyledSelectPopover,
  StyledChevron,
  IconWrapper,
  StyledAntiClockwiseArrowCircle,
  ResetButton,
  DatePicker,
  DatesWrapper,
} from './NotificationSearch.styles'
import { StyledSelectInput } from 'modules/shares/components/SearchBar/SearchBar.styles'

type NotificationSearchProps = {
  onSearchSubmit: (search: string) => void
}

type OptionsType = {
  comments: boolean
  replies: boolean
  mentions: boolean
  downloadableAssets: boolean
  starredUpdates: boolean
}

type OptionKey = keyof OptionsType

const getNewOptions = (): OptionsType => ({
  comments: false,
  replies: false,
  mentions: false,
  downloadableAssets: false,
  starredUpdates: false,
})

/**
 * COMPONENT
 */
export const NotificationSearch: React.FC<NotificationSearchProps> = ({
  onSearchSubmit,
}) => {
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<OptionsType>(getNewOptions())

  const updateAndSubmit = (newSearch: string) => {
    setSearch(newSearch)
    onSearchSubmit(newSearch)
  }

  const handleOptionUpdate = (option: OptionKey) => {
    let updatedSearch = search

    if (updatedSearch.includes(`type:${option}`)) {
      // remove option from search
      updatedSearch = updatedSearch
        .replace(`type:${option}`, '')
        .replace(/  +/g, ' ')
    } else {
      updatedSearch = `${updatedSearch} type:${option}`.replace(/  +/g, ' ')
    }

    setOptions({ ...options, [option]: !options[option] })
    updateAndSubmit(updatedSearch)
  }

  const handleReset = () => {
    setOptions(getNewOptions())

    // should reset clear the search or just clear the options?
    updateAndSubmit('')
  }

  const handleDateUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'from' | 'to'
  ) => {
    const date = e.target.value
    let updatedSearch = search

    if (updatedSearch.includes(`${type}:`)) {
      // remove previous date
      const startRemoval = updatedSearch.indexOf(type)
      updatedSearch = (
        updatedSearch.slice(0, startRemoval) +
        updatedSearch.slice(startRemoval + type.length + 11)
      ).replace(/  +/g, ' ')
    }

    if (date !== '') {
      updatedSearch = `${updatedSearch} ${type}:${date}`.replace(/  +/g, ' ')
    }

    updateAndSubmit(updatedSearch)
  }

  return (
    <Wrapper>
      <Filter
        onChange={setSearch}
        onSubmit={() => onSearchSubmit(search)}
        value={search}
        placeholder="Search updates"
      />
      <Options>
        <StyledSelectInput
          data-testid="filter-dropdown"
          value={'Filter'}
          onKeyUp={() => null}
        >
          <SelectTriggerUnstyled>
            <ToggleWrapper>
              Options
              <IconWrapper>
                <StyledChevron />
              </IconWrapper>
            </ToggleWrapper>
          </SelectTriggerUnstyled>
          <StyledSelectPopover position={positionDefault}>
            <Dropdown.Header>Only show</Dropdown.Header>
            <Box pl={5} pr={5} pb={4}>
              <Checkbox
                value={options.comments ? 1 : 0}
                name="comments"
                checked={options.comments}
                data-testid="updates-comments"
                label="Comments"
                onChange={() => handleOptionUpdate('comments')}
              />
              <Checkbox
                value={options.replies ? 1 : 0}
                name="replies"
                checked={options.replies}
                data-testid="updates-replies"
                label="Replies"
                onChange={() => handleOptionUpdate('replies')}
              />
              <Checkbox
                value={options.mentions ? 1 : 0}
                name="mentions"
                checked={options.mentions}
                data-testid="updates-mentions"
                label="Mentions"
                onChange={() => handleOptionUpdate('mentions')}
              />
              <Checkbox
                value={options.downloadableAssets ? 1 : 0}
                name="downloadableAssets"
                checked={options.downloadableAssets}
                data-testid="updates-downloadableAssets"
                label="Downloadable Assets"
                onChange={() => handleOptionUpdate('downloadableAssets')}
              />
              <Checkbox
                value={options.starredUpdates ? 1 : 0}
                name="starredUpdates"
                checked={options.starredUpdates}
                data-testid="updates-starredUpdates"
                label="Starred Updates"
                onChange={() => handleOptionUpdate('starredUpdates')}
              />
            </Box>
            <Separator />
            <Dropdown.Header>Between</Dropdown.Header>
            <DatesWrapper>
              <DatePicker
                type="date"
                placeholder="From"
                aria-label="from"
                onChange={e => handleDateUpdate(e, 'from')}
              />
              <span>and</span>
              <DatePicker
                type="date"
                placeholder="To"
                aria-label="to"
                onChange={e => handleDateUpdate(e, 'to')}
              />
            </DatesWrapper>
            <Separator />
            <ResetButton onClick={handleReset}>
              <StyledAntiClockwiseArrowCircle />
              Reset
            </ResetButton>
          </StyledSelectPopover>
        </StyledSelectInput>
      </Options>
    </Wrapper>
  )
}
